import React, { View } from "react-native";
import {
    Button,
    Form,
    InputControl,
    Loading,
    NumberInput,
    RequiredText,
    Switch,
    useForm,
    StyleFunction,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQIntegrationQuery,
    useEconomicSettingsSaveMutation,
    useIntegrationSettingsLazyQuery,
} from "graphql-sdk";
import { EconomicSettings, EconomicSettingsInput } from "lib";
import { useTranslation } from "locales";
import { useHandleMutationError } from "../../hooks";
import { schemaEconomicSettings } from "./forms";
import { useCallback, useEffect } from "react";

export function EconomicSettingsTab({
    integration,
}: {
    integration: GQIntegrationQuery["integration"];
}) {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const { handleMutationError } = useHandleMutationError();
    const [economicSettingsSave] = useEconomicSettingsSaveMutation();
    const form = useForm<EconomicSettingsInput>(schemaEconomicSettings, null);

    const [{ values }, { setDefaultValues }] = form;

    const edit = useCallback(async () => {
        if (
            !integration ||
            !integration.type ||
            integration.type !== "E-CONOMIC" ||
            !values
        ) {
            return;
        }

        await handleMutationError(async () => {
            const settingsInput: EconomicSettingsInput = {
                journalId: values.journalId,
                enableSyncByProductGroups: values.enableSyncByProductGroups,
                enableSyncAfterShiftClose: values.enableSyncAfterShiftClose,
            };

            await economicSettingsSave({
                variables: {
                    integrationId: integration.id,
                    settings: settingsInput,
                },
            });
        }, t("common.saved", "Saved"));
    }, [integration, values, handleMutationError, t, economicSettingsSave]);

    const [getIntegrationSettings, integrationSettings] =
        useIntegrationSettingsLazyQuery({
            fetchPolicy: "no-cache",
        });

    useEffect(() => {
        if (!integrationSettings?.data) {
            return;
        }

        const settings = integrationSettings.data?.integrationSettings;

        const defaultValues = {
            journalId: (settings as EconomicSettings).journalId ?? 1,
            enableSyncByProductGroups:
                (settings as EconomicSettings).enableSyncByProductGroups ||
                false,
            enableSyncAfterShiftClose:
                (settings as EconomicSettings).enableSyncAfterShiftClose ||
                false,
        };

        setDefaultValues(defaultValues);
    }, [integrationSettings, setDefaultValues]);

    useEffect(() => {
        if (!integration?.id) {
            return;
        }

        getIntegrationSettings({
            variables: { integrationId: integration.id },
        });
    }, [getIntegrationSettings, integration?.id]);

    if (
        !integration ||
        !integrationSettings.data ||
        integrationSettings.loading
    ) {
        return <Loading />;
    }

    return (
        <View style={styles.container}>
            <EconomicSettingsForm
                form={form}
                onSubmit={edit}
                submitButton={["common.save", "Save"]}
            />
        </View>
    );
}

function EconomicSettingsForm({
    form,
    onSubmit,
    submitButton,
}: {
    form: Form<EconomicSettingsInput>;
    onSubmit: () => Promise<void>;
    submitButton: [string, string];
}) {
    const [t] = useTranslation();
    const [{ values, errors }, { setValue, handleSubmit }] = form;

    if (!values) {
        return <Loading />;
    }

    return (
        <>
            <InputControl
                error={errors.journalId}
                description={t(
                    "backoffice.integrations.economic.journal_id_description",
                    "The id of the journal with e-conomic. Find it by going to the Visma e-conomic site and locate the id i.e. via the URL"
                )}
            >
                <NumberInput
                    label={t(
                        "backoffice.integrations.economic.journal_id",
                        "Journal id"
                    )}
                    placeholder={t(
                        "backoffice.integrations.economic.journal_id_enter",
                        "Enter journal ID from e-conomic"
                    )}
                    value={values.journalId}
                    onChange={value => setValue("journalId", value!)}
                    required={true}
                    min={0}
                    testID="journalId"
                />
            </InputControl>

            <InputControl
                description={t(
                    "backoffice.integrations.economic.enable_sync_by_product_groups_description",
                    "The legacy e-conomic sync routine uploads data by general accounts (see chart of accounts). It is used, if this toggle is switched off.\nThe newer sync routine syncs by accumulating sales by product groups (see chart of accounts). It is used, if this toggle is switched on."
                )}
            >
                <Switch
                    label={t(
                        "backoffice.integrations.economic.enable_sync_by_product_groups",
                        "Enable sync by product group"
                    )}
                    value={
                        (values as EconomicSettingsInput)
                            .enableSyncByProductGroups
                    }
                    onValueChange={value => {
                        setValue("enableSyncByProductGroups", Boolean(value));
                    }}
                    testID="enableSyncByProductGroups"
                />
            </InputControl>

            <InputControl
                description={t(
                    "backoffice.integrations.economic.enable_sync_after_shift_close_description",
                    "The shift data will be sync'ed to e-conomic right after the shift is closed. If this is disabled, the shift data can be sync'ed manually from the Shifts page here in Backoffice."
                )}
            >
                <Switch
                    label={t(
                        "backoffice.integrations.economic.enable_sync_after_shift_close",
                        "Enable sync after shift is closed"
                    )}
                    value={
                        (values as EconomicSettingsInput)
                            .enableSyncAfterShiftClose
                    }
                    onValueChange={value => {
                        setValue("enableSyncAfterShiftClose", Boolean(value));
                    }}
                    testID="enableSyncAfterShiftClose"
                />
            </InputControl>

            <Button onPress={handleSubmit(onSubmit)} testID="save">
                {t(submitButton[0], submitButton[1])}
            </Button>
            <RequiredText />
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        maxWidth: theme.dimensions.maxFormWidth,
    },
});
